import { ref, type Ref } from "vue";
import type FormCC from "@/interface/FormCC";
import type FormCCOtp from "@/interface/FormCCOtp";
import type MultiChannelLink from "@/interface/MultiChannelLink";
import type PopulatedIdInfo from "@/interface/PopulatedIdInfo";

//Product Bundling Data Auto Population
export default function setProductBundlingInfo(
  ccFormValue: Ref<FormCC>,
  prop: any,
  otp: Ref<FormCCOtp>,
  creditLimit: Ref<string>,
  multiChannel: Ref<MultiChannelLink>,
  biometricVerification: Ref<string>,
  populatedIdInfo: Ref<PopulatedIdInfo>,
  filteredMobile: (number: string) => string, // Accept the function as a parameter
): void {
  if ((prop.type == "PF_CC" || prop.type == "EP_CC") && prop.webFormDetails) {
    // assign product bundling
    if (prop.webFormDetails.dropOffStep)
      ccFormValue.value.dropOffStep = prop.webFormDetails.dropOffStep;
    if (prop.webFormDetails.ctosScore)
      ccFormValue.value.ctosScore = prop.webFormDetails.ctosScore;
    if (prop.webFormDetails.refNo)
      ccFormValue.value.refNo = prop.webFormDetails.refNo;
    if (prop.webFormDetails.emailVerification)
      if (prop.webFormDetails.emailVerification == "Y")
        otp.value.otpEmail.validated = true;
      else otp.value.otpEmail.validated = false;
    if (prop.webFormDetails.otpStatus)
      if (prop.webFormDetails.otpStatus == "Y")
        otp.value.otpMobile.validated = true;
      else otp.value.otpMobile.validated = false;
    if (prop.webFormDetails.webFormRequestId)
      ccFormValue.value.webFormRequestId = prop.webFormDetails.webFormRequestId;
    if (prop.webFormDetails.ekycStatus)
      ccFormValue.value.ekyc.status = prop.webFormDetails.ekycStatus;
    if (prop.webFormDetails.preAssessStatus)
      ccFormValue.value.ekyc.preStatus = prop.webFormDetails.preAssessStatus;
    if (prop.webFormDetails.dsrStatus)
      ccFormValue.value.dsrBurst = prop.webFormDetails.dsrStatus;
    if (prop.webFormDetails.creditLimit)
      creditLimit.value = prop.webFormDetails.creditLimit;

    // basic information
    if (prop.webFormDetails.webFormDetails.oriBasicInformation) {
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.dateOfBirth)
        ccFormValue.value.basicInformation.dob =
          prop.webFormDetails.webFormDetails.oriBasicInformation.dateOfBirth;
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.emailAddress)
        ccFormValue.value.basicInformation.email =
          prop.webFormDetails.webFormDetails.oriBasicInformation.emailAddress;
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.fullName)
        ccFormValue.value.basicInformation.name =
          prop.webFormDetails.webFormDetails.oriBasicInformation.fullName.toUpperCase();
      if (
        prop.webFormDetails.webFormDetails.oriBasicInformation
          .grossMonthlySalary
      )
        ccFormValue.value.basicInformation.grossSalary = Number(
          prop.webFormDetails.webFormDetails.oriBasicInformation
            .grossMonthlySalary,
        );
      if (
        prop.webFormDetails.webFormDetails.oriBasicInformation
          .homePhoneCountryCode
      )
        ccFormValue.value.basicInformation.homeCountryCode = filteredMobile(
          prop.webFormDetails.webFormDetails.oriBasicInformation
            .homePhoneCountryCode,
        );
      if (
        prop.webFormDetails.webFormDetails.oriBasicInformation.homePhoneNumber
      )
        ccFormValue.value.basicInformation.homePhoneNo =
          prop.webFormDetails.webFormDetails.oriBasicInformation.homePhoneNumber;
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.idNo)
        ccFormValue.value.basicInformation.idNo =
          prop.webFormDetails.webFormDetails.oriBasicInformation.idNo;
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.militaryNo)
        ccFormValue.value.basicInformation.militaryNo =
          prop.webFormDetails.webFormDetails.oriBasicInformation.militaryNo;
      if (
        prop.webFormDetails.webFormDetails.oriBasicInformation.mobileCountryCode
      )
        ccFormValue.value.basicInformation.countryCode = filteredMobile(
          prop.webFormDetails.webFormDetails.oriBasicInformation
            .mobileCountryCode,
        );
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.mobileNumber)
        ccFormValue.value.basicInformation.mobileNo =
          prop.webFormDetails.webFormDetails.oriBasicInformation.mobileNumber;
      if (
        prop.webFormDetails.webFormDetails.oriBasicInformation.netMonthlySalary
      )
        ccFormValue.value.basicInformation.netSalary = Number(
          prop.webFormDetails.webFormDetails.oriBasicInformation
            .netMonthlySalary,
        );
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.oldIc)
        ccFormValue.value.basicInformation.oldIc =
          prop.webFormDetails.webFormDetails.oriBasicInformation.oldIc;
      if (
        prop.webFormDetails.webFormDetails.oriBasicInformation.professionOthers
      )
        ccFormValue.value.basicInformation.others =
          prop.webFormDetails.webFormDetails.oriBasicInformation.professionOthers;
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.idTypeCode)
        ccFormValue.value.basicInformation.idType =
          prop.webFormDetails.webFormDetails.oriBasicInformation.idTypeCode;
      if (
        prop.webFormDetails.webFormDetails.oriBasicInformation.nationalityCode
      )
        ccFormValue.value.basicInformation.nationality =
          prop.webFormDetails.webFormDetails.oriBasicInformation.nationalityCode;
      if (prop.webFormDetails.webFormDetails.oriBasicInformation.professionCode)
        ccFormValue.value.basicInformation.profession =
          prop.webFormDetails.webFormDetails.oriBasicInformation.professionCode;
    }

    //emergency contact info
    if (prop.webFormDetails.webFormDetails.oriEmergencyContactInfo) {
      if (
        prop.webFormDetails.webFormDetails.oriEmergencyContactInfo.contactName
      )
        ccFormValue.value.emergencyContact.relativeName =
          prop.webFormDetails.webFormDetails.oriEmergencyContactInfo.contactName;
      if (prop.webFormDetails.webFormDetails.oriEmergencyContactInfo.contactNo)
        ccFormValue.value.emergencyContact.mobileNo =
          prop.webFormDetails.webFormDetails.oriEmergencyContactInfo.contactNo;
      if (
        prop.webFormDetails.webFormDetails.oriEmergencyContactInfo
          .contactNoCountryCode
      )
        ccFormValue.value.emergencyContact.countryCode = filteredMobile(
          prop.webFormDetails.webFormDetails.oriEmergencyContactInfo
            .contactNoCountryCode,
        );
      if (
        prop.webFormDetails.webFormDetails.oriEmergencyContactInfo.relationship
      )
        ccFormValue.value.emergencyContact.relationship =
          prop.webFormDetails.webFormDetails.oriEmergencyContactInfo.relationship;
      if (prop.webFormDetails.webFormDetails.oriEmergencyContactInfo.title)
        ccFormValue.value.emergencyContact.title =
          prop.webFormDetails.webFormDetails.oriEmergencyContactInfo.title;
    }
    //personInformation
    if (prop.webFormDetails.webFormDetails.oriPersonInformation) {
      // if (
      //   prop.webFormDetails.webFormDetails.personInformation
      //     .academicQualification
      // )
      //   ccFormValue.value.value.personalInformation.academic =
      //     prop.webFormDetails.webFormDetails.personInformation.academicQualification;
      if (prop.webFormDetails.webFormDetails.oriPersonInformation.genderCode)
        ccFormValue.value.personalInformation.gender =
          prop.webFormDetails.webFormDetails.oriPersonInformation.genderCode;
      if (
        prop.webFormDetails.webFormDetails.oriPersonInformation
          .maritalStatusCode
      )
        ccFormValue.value.personalInformation.maritalStatus =
          prop.webFormDetails.webFormDetails.oriPersonInformation.maritalStatusCode;
      // if (prop.webFormDetails.webFormDetails.personInformation.motherMaidenName)
      //   ccFormValue.value.value.personalInformation.motherMaidenName =
      //     prop.webFormDetails.webFormDetails.personInformation.motherMaidenName;
      if (
        prop.webFormDetails.webFormDetails.oriPersonInformation
          .numberOfDependents != null
      )
        ccFormValue.value.personalInformation.dependency =
          prop.webFormDetails.webFormDetails.oriPersonInformation.numberOfDependents.toString();
      // if (
      //   prop.webFormDetails.webFormDetails.personInformation.otherQualification
      // )
      //   ccFormValue.value.value.personalInformation.otherAcademic =
      //     prop.webFormDetails.webFormDetails.personInformation.otherQualification;
      if (prop.webFormDetails.webFormDetails.oriPersonInformation.raceCode)
        ccFormValue.value.personalInformation.race =
          prop.webFormDetails.webFormDetails.oriPersonInformation.raceCode;
      if (prop.webFormDetails.webFormDetails.oriPersonInformation.otherRace)
        ccFormValue.value.personalInformation.otherRace =
          prop.webFormDetails.webFormDetails.oriPersonInformation.otherRace;
      if (
        prop.webFormDetails.webFormDetails.oriPersonInformation
          .residencyStatusCode
      )
        ccFormValue.value.personalInformation.residencyStatus =
          prop.webFormDetails.webFormDetails.oriPersonInformation.residencyStatusCode;
      if (
        prop.webFormDetails.webFormDetails.oriPersonInformation
          .residentialOwnership
      )
        ccFormValue.value.personalInformation.residentialOwnership =
          prop.webFormDetails.webFormDetails.oriPersonInformation.residentialOwnership;
      if (prop.webFormDetails.webFormDetails.oriPersonInformation.title)
        ccFormValue.value.personalInformation.title =
          prop.webFormDetails.webFormDetails.oriPersonInformation.title;
      if (
        prop.webFormDetails.webFormDetails.oriPersonInformation.permanentAddress
      ) {
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .permanentAddress.addressLine1
        ) {
          ccFormValue.value.personalInformation.permanentAddress1 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.permanentAddress.addressLine1.toUpperCase();
          ccFormValue.value.personalInformation.question1 = "No";
        } else ccFormValue.value.personalInformation.question1 = "Yes";
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .permanentAddress.addressLine2
        )
          ccFormValue.value.personalInformation.permanentAddress2 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.permanentAddress.addressLine2.toUpperCase();
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .permanentAddress.addressLine3
        )
          ccFormValue.value.personalInformation.permanentAddress3 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.permanentAddress.addressLine3.toUpperCase();
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .permanentAddress.city
        )
          ccFormValue.value.personalInformation.city2 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.permanentAddress.city;
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .permanentAddress.postcode
        )
          ccFormValue.value.personalInformation.postCode2 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.permanentAddress.postcode;
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .permanentAddress.state
        )
          ccFormValue.value.personalInformation.state2 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.permanentAddress.state;
      } else ccFormValue.value.personalInformation.question1 = "Yes";
      if (
        prop.webFormDetails.webFormDetails.oriPersonInformation
          .residentialAddress
      ) {
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .residentialAddress.addressLine1
        )
          ccFormValue.value.personalInformation.residentialAddress1 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.residentialAddress.addressLine1.toUpperCase();
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .residentialAddress.addressLine2
        )
          ccFormValue.value.personalInformation.residentialAddress2 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.residentialAddress.addressLine2.toUpperCase();
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .residentialAddress.addressLine3
        )
          ccFormValue.value.personalInformation.residentialAddress3 =
            prop.webFormDetails.webFormDetails.oriPersonInformation.residentialAddress.addressLine3.toUpperCase();
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .residentialAddress.city
        )
          ccFormValue.value.personalInformation.city =
            prop.webFormDetails.webFormDetails.oriPersonInformation.residentialAddress.city;
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .residentialAddress.postcode
        )
          ccFormValue.value.personalInformation.postCode =
            prop.webFormDetails.webFormDetails.oriPersonInformation.residentialAddress.postcode;
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .residentialAddress.state
        )
          ccFormValue.value.personalInformation.state =
            prop.webFormDetails.webFormDetails.oriPersonInformation.residentialAddress.state;
        if (
          prop.webFormDetails.webFormDetails.oriPersonInformation
            .residentialAddress.durationOfStay
        ) {
          if (
            prop.webFormDetails.webFormDetails.oriPersonInformation
              .residentialAddress.durationOfStay.month != null
          )
            ccFormValue.value.personalInformation.monthStay =
              prop.webFormDetails.webFormDetails.oriPersonInformation.residentialAddress.durationOfStay.month.toString();
          if (
            prop.webFormDetails.webFormDetails.oriPersonInformation
              .residentialAddress.durationOfStay.year != null
          )
            ccFormValue.value.personalInformation.yearStay =
              prop.webFormDetails.webFormDetails.oriPersonInformation.residentialAddress.durationOfStay.year.toString();
        }
      }
    }
    //principalCard
    if (prop.webFormDetails.webFormDetails.oriPrincipalCardDetail) {
      if (prop.webFormDetails.webFormDetails.oriPrincipalCardDetail.eStatement)
        ccFormValue.value.principalDetails.eStatement = prop.webFormDetails
          .webFormDetails.oriPrincipalCardDetail.eStatement
          ? "Yes"
          : "No";
    }

    // employmentDetail
    if (prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo) {
      if (prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.areaCode)
        ccFormValue.value.employmentDetails.areaCode =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.areaCode;
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyName
      )
        ccFormValue.value.employmentDetails.companyName =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyName;
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
          .companyPhoneNo
      )
        ccFormValue.value.employmentDetails.phoneNo =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyPhoneNo;
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
          .companyPhoneNoCountryCode
      )
        ccFormValue.value.employmentDetails.countryCode = filteredMobile(
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .companyPhoneNoCountryCode,
        );
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
          .employmentType
      )
        ccFormValue.value.employmentDetails.employment =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.employmentType;
      if (prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.incomeType)
        ccFormValue.value.employmentDetails.employmentIncome =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.incomeType;
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.my2ndHome !=
        null
      ) {
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .my2ndHome == true
        )
          ccFormValue.value.employmentDetails.question1 = "Yes";
        else ccFormValue.value.employmentDetails.question1 = "No";
      }
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
          .natureOfBusiness
      )
        ccFormValue.value.employmentDetails.natureBusiness =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.natureOfBusiness;
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
          .occupationType
      )
        ccFormValue.value.employmentDetails.occupation =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.occupationType;
      if (prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.otherNOB)
        ccFormValue.value.employmentDetails.others =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.otherNOB;
      if (prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.workInMY)
        ccFormValue.value.employmentDetails.question2 =
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.workInMY ==
          "true"
            ? "Yes"
            : "No";
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
          .companyAddress
      ) {
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.addressLine1
        )
          ccFormValue.value.employmentDetails.companyAddress1 =
            prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyAddress.addressLine1.toUpperCase();
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.addressLine2
        )
          ccFormValue.value.employmentDetails.companyAddress2 =
            prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyAddress.addressLine2.toUpperCase();
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.addressLine3
        )
          ccFormValue.value.employmentDetails.companyAddress3 =
            prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyAddress.addressLine3.toUpperCase();
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.city
        )
          ccFormValue.value.employmentDetails.city =
            prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyAddress.city;
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.postcode
        )
          ccFormValue.value.employmentDetails.postCode =
            prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyAddress.postcode;
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .companyAddress.state
        )
          ccFormValue.value.employmentDetails.state =
            prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.companyAddress.state;
      }
      if (
        prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
          .lengthOfEmployment
      ) {
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .lengthOfEmployment.month
        )
          ccFormValue.value.employmentDetails.monthEmp =
            prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.lengthOfEmployment.month;
        if (
          prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo
            .lengthOfEmployment.year
        )
          ccFormValue.value.employmentDetails.yearEmp =
            prop.webFormDetails.webFormDetails.oriEmploymentDetailInfo.lengthOfEmployment.year;
      }
    }

    const newFormData = new FormData();
    newFormData.append("", "");

    if (prop.type == "PF_CC") {
      ccFormValue.value.uploadDocument.proofIdentity.file.push(newFormData);
      ccFormValue.value.uploadDocument.proofIdentity.fileName.push(
        "Personal Financing Documents",
      );
      ccFormValue.value.uploadDocument.proofIdentity.fileNumber += 1;
      ccFormValue.value.uploadDocument.proofIdentity.resumeFileNumber += 1;
      ccFormValue.value.uploadDocument.proofIncome.file.push(newFormData);
      ccFormValue.value.uploadDocument.proofIncome.fileName.push(
        "Personal Financing Documents",
      );
      ccFormValue.value.uploadDocument.proofIncome.fileNumber += 1;
      ccFormValue.value.uploadDocument.proofIncome.resumeFileNumber += 1;
    } else if (
      prop.type == "EP_CC" &&
      prop.webFormDetails.epccDocumentNameDTO
    ) {
      if (
        prop.webFormDetails.epccDocumentNameDTO.proofOfIdentityFileName != null
      ) {
        if (
          prop.webFormDetails.epccDocumentNameDTO.proofOfIdentityFileName
            .length > 0
        ) {
          for (
            let i = 0;
            i <
            prop.webFormDetails.epccDocumentNameDTO.proofOfIdentityFileName
              .length;
            i++
          ) {
            ccFormValue.value.EPCCUploadDocument.proofIdentity.file.push(
              newFormData,
            );
            ccFormValue.value.EPCCUploadDocument.proofIdentity.fileName.push(
              prop.webFormDetails.epccDocumentNameDTO.proofOfIdentityFileName[
                i
              ],
            );
            ccFormValue.value.EPCCUploadDocument.proofIdentity.invalidFile.push(
              false,
            );
            ccFormValue.value.EPCCUploadDocument.proofIdentity.fileNumber += 1;
            ccFormValue.value.EPCCUploadDocument.proofIdentity.resumeFileNumber += 1;
          }
        }
      }

      if (
        prop.webFormDetails.epccDocumentNameDTO.proofOfIncomeFileName != null
      ) {
        if (
          prop.webFormDetails.epccDocumentNameDTO.proofOfIncomeFileName.length >
          0
        ) {
          for (
            let i = 0;
            i <
            prop.webFormDetails.epccDocumentNameDTO.proofOfIncomeFileName
              .length;
            i++
          ) {
            ccFormValue.value.EPCCUploadDocument.proofIncome.file.push(
              newFormData,
            );
            ccFormValue.value.EPCCUploadDocument.proofIncome.fileName.push(
              prop.webFormDetails.epccDocumentNameDTO.proofOfIncomeFileName[i],
            );
            ccFormValue.value.EPCCUploadDocument.proofIncome.invalidFile.push(
              false,
            );
            ccFormValue.value.EPCCUploadDocument.proofIncome.fileNumber += 1;
            ccFormValue.value.EPCCUploadDocument.proofIncome.resumeFileNumber += 1;
          }
        }
      }
    }

    if (prop.webFormDetails.merchantCode)
      multiChannel.value.mc = prop.webFormDetails.merchantCode;

    if (prop.webFormDetails.biometricVerification)
      biometricVerification.value = "true";

    //terms
    if (prop.webFormDetails.webFormDetails.terms) {
      if (prop.webFormDetails.webFormDetails.terms.taxIdentificationNo) {
        ccFormValue.value.declaration.taxpayer =
          prop.webFormDetails.webFormDetails.terms.taxIdentificationNo;
        populatedIdInfo.value.terms.taxIdentificationNo =
          prop.webFormDetails.webFormDetails.terms.taxIdentificationNo;
      }
    }
  }
}
